import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Check } from 'lucide-react'; // Assuming you are using lucide-react

gsap.registerPlugin(ScrollTrigger);

const ExecutionSection = ({ offerings }) => {
    const sectionRef = useRef(null);
    const bgRef = useRef(null);
    const headingRef = useRef(null);
    const cardsRef = useRef([]);

    useEffect(() => {
        const ctx = gsap.context(() => {
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: sectionRef.current,
                    start: "top 75%", // Triggers when top of section is 75% down viewport
                    toggleActions: "play none none none",
                }
            });

            // 1. Background Expansion (The Stage)
            tl.from(bgRef.current, {
                scale: 0.92,
                opacity: 0,
                duration: 1,
                ease: "power3.out"
            });

            // 2. Heading Reveal
            tl.from(headingRef.current, {
                y: 30,
                opacity: 0,
                duration: 0.6,
                ease: "power2.out"
            }, "-=0.6");

            // 3. Loop through cards to animate them and their contents
            cardsRef.current.forEach((card, index) => {
                if (!card) return;

                // Select internal elements using scoped standard DOM queries
                const image = card.querySelector('.card-img');
                const contentBlock = card.querySelector('.card-content');
                const features = card.querySelectorAll('.feature-item');
                const buttonArea = card.querySelector('.card-btn-area');

                // Animate the Card Container itself
                tl.from(card, {
                    y: 60,
                    opacity: 0,
                    duration: 0.8,
                    ease: "power2.out"
                }, index === 0 ? "-=0.4" : "-=0.6"); // Overlap the second card slightly

                // Image Reveal (Scale down effect)
                tl.from(image, {
                    scale: 1.2,
                    duration: 1.2,
                    ease: "power2.out"
                }, "<"); // Starts same time as card appears

                // Text Content Slide
                tl.from(contentBlock, {
                    y: 20,
                    opacity: 0,
                    duration: 0.5
                }, "-=0.8");

                // Staggered Features (The Checkmark Cascade)
                if (features.length > 0) {
                    tl.from(features, {
                        x: -20,
                        opacity: 0,
                        stagger: 0.1, // Fast stagger for list items
                        duration: 0.4,
                        ease: "back.out(1.5)" // Small bounce
                    }, "-=0.4");
                }

                // Button Pop
                tl.from(buttonArea, {
                    y: 10,
                    opacity: 0,
                    duration: 0.4
                }, "-=0.2");
            });

        }, sectionRef);

        return () => ctx.revert();
    }, []);

    // Helper to add refs
    const addToCardRefs = (el) => {
        if (el && !cardsRef.current.includes(el)) {
            cardsRef.current.push(el);
        }
    };

    return (
        <section ref={sectionRef} className="container max-w-7xl mx-auto px-6 md:px-12 my-[100px]">
            {/* Background Container */}
            <div 
                ref={bgRef}
                style={{ background: 'radial-gradient(at top center, #182186 0%, #02235c 100%)' }} 
                className='py-10 px-7 rounded-[20px] will-change-transform'
            >
                <div className="max-w-4xl mx-auto text-center mb-12">
                    <h1 ref={headingRef} className="text-[32px] leading-[50px] font-[600] text-[#FFFFFF] text-center">
                        From Ideas to Execution, We Build Together.
                    </h1>
                </div>

                {/* Two-Column Card Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-7">
                    {offerings && offerings.map((offering, index) => (
                        <div
                            key={index}
                            ref={addToCardRefs}
                            className="bg-white rounded-[20px] overflow-hidden grid grid-rows-[max-content_1fr]"
                        >
                            {/* Image Section */}
                            <div className='w-full h-full min-[768px]:h-[400px] min-[2560px]:h-[500px] p-5 overflow-hidden'>
                                <img
                                    src={offering.image}
                                    alt={`Illustration for ${offering.tag}`}
                                    // Added 'card-img' class for GSAP targeting
                                    className="card-img w-full h-full object-cover rounded-[18px]"
                                />
                            </div>

                            {/* Content Section */}
                            <div className="p-5 grid grid-rows-[1fr_max-content]">
                                {/* Wrapped text in a div with class 'card-content' for animation group */}
                                <div className='card-content'>
                                    {/* Tag & Title */}
                                    <p className="text-[#000000] text-[26px] font-[500] leading-[40px]">{offering.tag}</p>
                                    <h2 className="text-[30px] font-[600] font-bold mb-6 text-[#0637E0]">{offering.title}</h2>
                                    <p className="font-[700] text-[#000000] mb-5">What you get</p>

                                    {/* Feature List */}
                                    <ul className="flex-grow">
                                        {offering.features.map((feature, idx) => (
                                            <li 
                                                key={idx} 
                                                // Added 'feature-item' class for stagger animation
                                                className="feature-item flex items-center space-x-3 mb-3 text-gray-700 pr-10"
                                            >
                                                <Check className="w-4 h-4 text-[#0637E0] mt-1 flex-shrink-0" />
                                                <span className="text-[14px] text-[#374151] leading-[20px] font-[600]">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Button & Note */}
                                {/* Added 'card-btn-area' for animation */}
                                <div className="card-btn-area mt-8 flex flex-col sm:grid grid-cols-[max-content_1fr] sm:items-center">
                                    <a href={offering.linkUrl} className="px-6 py-2 max-sm:text-center sm:py-1 rounded-full font-[500] text-[13px] transition-colors duration-200 shadow-md mb-4 sm:mb-0 sm:mr-4 bg-[#0D3BD1] text-[#FFFFFF]">
                                        {offering.buttonText}
                                    </a>
                                    <div className='flex flex-col'>
                                        {offering.buttonNote.map((offer , index) => (
                                            <p className="text-[13px] font-[600] italic text-[#374151]">{offer}</p>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default ExecutionSection;